/**
 * 门户全部可定义配置（存 lf-auth tenant_config / tenant_system_config）
 * 门户通过 getDomainParaJson → 经 toPortalOemInfo 扁平化为旧 getByDomain 兼容结构 + 扩展字段
 *
 * 字段来源整合：
 * A) 原 basic tenantPortalConfig/getByDomain 返回体
 * B) suiyue-web portal 写死参数（短信 Action、OAuth、终端选项、客服区等）
 * C) 登录后 sysConfigMap / IndexParam 主题色
 */

/** —— A) 原 getByDomain 基础字段（与历史接口对齐） —— */
export const PORTAL_LEGACY_BASE_FIELDS = [
  {
    key: 'domain',
    label: '门户域名',
    tip: '须与 Referer 一致：https://host[:port]，无 path；多域名逗号分隔',
    defaultValue: '',
  },
  {
    key: 'copyright',
    label: '版权信息',
    tip: '登录页底部，可用 | 换行',
    defaultValue: '',
  },
  {
    key: 'loginLogo',
    label: '登录Logo',
    tip: '登录页与登录后顶栏 Logo（getDomainParaJson 的 loginLogo/systemLogo/sysLogo 同源）',
    type: 'image',
    defaultValue: '',
  },
  {
    key: 'favicon',
    label: '浏览器图标 favicon',
    tip: '浏览器标签 ico/png；空则用默认 /images/suiyue.ico',
    type: 'image',
    defaultValue: '',
  },
  {
    key: 'systemImage',
    label: '登录页背景图',
    tip: '多张逗号分隔；门户读出后转为 bgimages[]',
    type: 'image',
    defaultValue: '',
  },
  {
    key: 'sysImage',
    label: '应用列表背景图',
    tip: '系统选择列表封面；多个应用共用这一张（对应 systemlist sysImage）',
    type: 'image',
    defaultValue: '',
  },
  {
    key: 'oemName',
    label: '门户名称 oemName',
    tip: '展示用；浏览器标题优先用 systemName',
    defaultValue: '',
  },
  {
    key: 'oemCode',
    label: '全局 oemCode',
    tip: '门户唯一业务编码（登录/短信/机构/菜单等共用），如 zjsm',
    defaultValue: '',
  },
  {
    key: 'serverAddr',
    label: '服务器地址',
    tip: '可选',
    defaultValue: '',
  },
  {
    key: 'logo',
    label: '平台Logo',
    tip: '管理端展示用',
    type: 'image',
    defaultValue: '',
  },
  {
    key: 'wxAppletAppCode',
    label: '二维码应用编码',
    tip: '用于 createWxAppletQr 的 appCode；空则接口不传 appCode',
    defaultValue: '',
  },
]

/** 主题色（默认取自随约生产 getByDomain） */
export const PORTAL_COLOR_FIELDS = [
  { key: 'headerBgcolor', label: '表头背景颜色', tip: '默认 #F7F8F2', defaultValue: '#F7F8F2', type: 'color' },
  { key: 'headerFontcolor', label: '表头字体颜色', tip: '默认 #666666', defaultValue: '#666666', type: 'color' },
  { key: 'headerSelectedBgcolor', label: '表头选中背景色', tip: '默认 #23B16F', defaultValue: '#23B16F', type: 'color' },
  { key: 'headerSelectedFontcolor', label: '表头选中字体色', tip: '默认 #FFFFFF', defaultValue: '#FFFFFF', type: 'color' },
  { key: 'leftMenuBgcolor', label: '左侧菜单背景色', tip: '默认 #FFF5F3', defaultValue: '#FFF5F3', type: 'color' },
  { key: 'leftMenuitemBgcolor', label: '左侧菜单子项色', tip: '默认 #2d8cf0', defaultValue: '#2d8cf0', type: 'color' },
  { key: 'leftMenuFontcolor', label: '左侧菜单字体色', tip: '默认 #999999', defaultValue: '#999999', type: 'color' },
  { key: 'leftMenuSelectedBgcolor', label: '左侧菜单选中背景', tip: '默认 #23B16F', defaultValue: '#23B16F', type: 'color' },
  { key: 'leftMenuSelecteFontcolor', label: '左侧菜单选中字体', tip: '默认 #FFFFFF', defaultValue: '#FFFFFF', type: 'color' },
]

/** 登录页扩展 */
export const PORTAL_LOGIN_EXTRA_FIELDS = [
  { key: 'loginPanelBg', label: '登录框顶部图', tip: '原 /images/loginbg.jpg', type: 'image', defaultValue: '/images/loginbg.jpg' },
  { key: 'loginBtnBgcolor', label: '登录按钮背景色', tip: '如 #CB0200；空则用按钮类型默认色', defaultValue: '', type: 'color' },
  { key: 'loginBtnBorderColor', label: '登录按钮边框色', tip: '如 #CB0200', defaultValue: '', type: 'color' },
  { key: 'loginBtnFontcolor', label: '登录按钮字体色', tip: '如 #ffffff', defaultValue: '', type: 'color' },
  { key: 'loginBtnType', label: '默认登录按钮类型', tip: 'success / primary / default', defaultValue: 'success' },
  { key: 'enablePasswordLogin', label: '启用账密登录', tip: '1=启用 0=禁用', defaultValue: '1' },
  { key: 'enableQrcodeLogin', label: '启用扫码登录', tip: '1=启用 0=禁用', defaultValue: '1' },
  { key: 'defaultLoginType', label: '默认登录方式', tip: 'password / qrcode', defaultValue: 'qrcode' },
  { key: 'loginTypePasswordIcon', label: '账密登录切换图标', tip: '原 /images/lg_compute.png', type: 'image', defaultValue: '/images/lg_compute.png' },
  { key: 'loginTypeQrcodeIcon', label: '扫码登录切换图标', tip: '原 /images/lg_qrcode.png', type: 'image', defaultValue: '/images/lg_qrcode.png' },
  { key: 'loginTypePasswordMsg', label: '账密登录切换提示', tip: '', defaultValue: '切换至密码登录' },
  { key: 'loginTypeQrcodeMsg', label: '扫码登录切换提示', tip: '', defaultValue: '切换至扫码登录' },
  { key: 'passwordResetText', label: '密码重置文案', tip: '', defaultValue: '密码重置' },
  { key: 'qrcodeLoginTip', label: '扫码登录提示文案', tip: '', defaultValue: '请使用微信扫码登录' },
]

/** 客服/申请通道（原 oemInfo.id==1） */
export const PORTAL_SERVICE_FIELDS = [
  { key: 'showServiceBox', label: '显示客服申请区', tip: '1=显示（兼容原 id==1）', defaultValue: '0' },
  { key: 'serviceWechatTitle', label: '客服扫码标题', tip: '', defaultValue: '请用微信手机端扫码咨询客服' },
  { key: 'serviceWechatQrcode', label: '客服二维码图', tip: '原 /images/we_qrcode.jpg', type: 'image', defaultValue: '/images/we_qrcode.jpg' },
  { key: 'servicePhone', label: '客服电话', tip: '原 18148789092', defaultValue: '18148789092' },
  { key: 'servicePhoneLabel', label: '客服电话前缀', tip: '', defaultValue: '客服电话：' },
  { key: 'applyTitle', label: '申请通道标题', tip: '', defaultValue: '管理员申请通道' },
  { key: 'applyStep1Text', label: '申请步骤一文案', tip: '', defaultValue: '第一步下载申请表填写并盖章' },
  { key: 'applyDoc1Name', label: '申请表1名称', tip: '', defaultValue: '随约系统管理员授权备案表-下载' },
  { key: 'applyDoc1Url', label: '申请表1下载地址', tip: '完整 URL', defaultValue: 'https://liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm/20230811/847933196.doc' },
  { key: 'applyDoc1FileName', label: '申请表1文件名', tip: '', defaultValue: '随约系统管理员授权备案表.doc' },
  { key: 'applyDoc2Name', label: '申请表2名称', tip: '', defaultValue: '机构-随约系统管理员授权备案表-下载' },
  { key: 'applyDoc2Url', label: '申请表2下载地址', tip: '完整 URL', defaultValue: 'https://liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm/20230811/944352499.doc' },
  { key: 'applyDoc2FileName', label: '申请表2文件名', tip: '', defaultValue: '机构-随约系统管理员授权备案表.doc' },
  { key: 'applyStep2Text', label: '申请步骤二文案', tip: '', defaultValue: '第二步扫码填写并拍照上传申请表图片' },
  { key: 'applyQrcodeTitle', label: '申请扫码标题', tip: '', defaultValue: '随约系统管理员授权备案表' },
  { key: 'applyQrcodeImage', label: '申请二维码图', tip: '原 /images/apply_qrcode.jpg', type: 'image', defaultValue: '/images/apply_qrcode.jpg' },
]

/** 登录鉴权 */
export const PORTAL_AUTH_FIELDS = [
  { key: 'appCode', label: '登录应用编码 appCode', tip: '原 suiyue_pc', defaultValue: 'suiyue_pc' },
  { key: 'oauthClientId', label: 'OAuth client_id', tip: '原 suianju', defaultValue: 'suianju' },
  { key: 'oauthClientSecret', label: 'OAuth client_secret', tip: '原 1234567', defaultValue: '1234567' },
  { key: 'oauthScope', label: 'OAuth scope', tip: '原 all', defaultValue: 'all' },
  { key: 'oauthGrantType', label: 'OAuth grant_type', tip: '原 password', defaultValue: 'password' },
  { key: 'passwordLoginType', label: '账密 loginType', tip: 'suiyue-web 用 9；plus 用 4；空则门户兜底', defaultValue: '' },
  { key: 'qrcodeLoginType', label: '扫码 loginType', tip: '原 10；空则门户兜底 10', defaultValue: '' },
  {
    key: 'captchaAppletOrgCode',
    label: '扫码登录 orgCode',
    tip: '可选。有值则扫码登录接口带上 orgCode；多数租户留空即可（已无需按域名关键词判断）',
    defaultValue: '',
  },
]

/** 终端选项候选（多选） */
export const TERMINAL_OPTION_CATALOG = [
  { label: '0', text: '全部' },
  { label: '1', text: 'TV' },
  { label: '3', text: 'APP' },
  { label: '4', text: '随约小程序' },
  { label: '7', text: '滨江未来家' },
  { label: '8', text: '慈爱一键通' },
  { label: '9', text: '雷锋网' },
  { label: '10', text: '惠农心' },
]

/** 二维码宣传海报默认背景图组（allIndex / qrcodeindex / fastcustomization 等共用） */
export const DEFAULT_QRCODE_BG_IMAGES = [
  '/images/pic_hd1.png',
  '/images/pic_hd2.png',
  '/images/pic_hd3.png',
  '/images/pic_hd4.png',
  '/images/pic_hd5.png',
  '/images/pic_hd6.png',
]

/** 业务 Action（一套配置，不再按域名切换备用） */
export const PORTAL_ACTION_FIELDS = [
  {
    key: 'smsUpdatePwdAction',
    label: '重置密码 Action',
    tip: '发短信与 forgetPwd 校验共用，如 SD_UPDATAPWD_MSG',
    defaultValue: 'SD_UPDATAPWD_MSG',
  },
  {
    key: 'smsUpdatePwdParamString',
    label: '重置密码短信 paramString',
    tip: 'JSON 字符串',
    defaultValue: '{"code":null,"product":"社区随约服务网上驿站"}',
  },
  {
    key: 'terminalOptions',
    label: '终端选项',
    tip: '多选；存 label 逗号串，门户展开为 options',
    type: 'terminalMulti',
    defaultValue: '1,3,4',
  },
  {
    key: 'qrcodeBgImages',
    label: '二维码宣传背景图组',
    tip: '最多 30 张；用于 allIndex / qrcodeindex / fastcustomization 等海报背景；空则用默认 pic_hd1~6',
    type: 'imageMulti',
    maxCount: 30,
    defaultValue: DEFAULT_QRCODE_BG_IMAGES.join(','),
  },
]

/** 布局 */
export const PORTAL_LAYOUT_FIELDS = [
  { key: 'headerHeight', label: '顶栏高度(px)', tip: '原 60', defaultValue: '60' },
  { key: 'leftMenuWidth', label: '左侧菜单宽度(px)', tip: '原 200', defaultValue: '200' },
  { key: 'sysListHeaderBgcolor', label: '应用列表顶栏背景', tip: '原 #515a6e', defaultValue: '#515a6e', type: 'color' },
  { key: 'sysListHeaderFontcolor', label: '应用列表顶栏字体', tip: '原 #ffffff', defaultValue: '#ffffff', type: 'color' },
  { key: 'copyrightAreaBgcolor', label: '版权区背景色', tip: '原 #f9f9f9', defaultValue: '#f9f9f9', type: 'color' },
  { key: 'copyrightAreaFontcolor', label: '版权区字体色', tip: '原 #555555', defaultValue: '#555555', type: 'color' },
  { key: 'defaultMenuIcon', label: '默认菜单图标', tip: '原 /images/nopic.png', type: 'image', defaultValue: '/images/nopic.png' },
  { key: 'defaultHomePage', label: '默认首页路径', tip: '无 sysHomePage 时兜底', defaultValue: '/homepage' },
]

/** 全部可配置字段（管理端/校验用） */
export const ALL_PORTAL_CONFIG_FIELDS = [
  ...PORTAL_LEGACY_BASE_FIELDS,
  ...PORTAL_COLOR_FIELDS,
  ...PORTAL_LOGIN_EXTRA_FIELDS,
  ...PORTAL_SERVICE_FIELDS,
  ...PORTAL_AUTH_FIELDS,
  ...PORTAL_ACTION_FIELDS,
  ...PORTAL_LAYOUT_FIELDS,
]

function fieldsToDefaults(fields) {
  return fields.reduce((acc, f) => {
    acc[f.key] = f.defaultValue != null ? f.defaultValue : ''
    return acc
  }, {})
}

/**
 * 随约生产环境历史定制样例（用户提供的 getByDomain 数据 + 扩展默认）
 * 管理端可一键填充；保存后写入 tenant_config，由 getDomainParaJson 返回
 */
export const ZJSM_PORTAL_PRESET = {
  domain: 'https://suiyue.liefengtech.com',
  copyright: '广州列丰信息科技有限公司技术支持',
  loginLogo: 'https://property-liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm_oldpeople/20210224/2021022417092200PvYYMj.png',
  systemLogo: 'https://property-liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm_oldpeople/20210224/2021022417092200PvYYMj.png',
  systemImage: 'https://property-liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm_oldpeople/20210224/202102241701029SFPutxM.png',
  loginBackground: 'https://property-liefeng.oss-cn-shenzhen.aliyuncs.com/PROD/zjsm_oldpeople/20210224/202102241701029SFPutxM.png',
  oemName: '随约平台',
  headerBgcolor: '#F7F8F2',
  headerFontcolor: '#666666',
  headerSelectedBgcolor: '#23B16F',
  headerSelectedFontcolor: '#FFFFFF',
  leftMenuBgcolor: '#FFF5F3',
  leftMenuFontcolor: '#999999',
  leftMenuSelectedBgcolor: '#23B16F',
  leftMenuSelecteFontcolor: '#FFFFFF',
  leftMenuitemBgcolor: '#2d8cf0',
  showServiceBox: '1',
  oemCode: 'zjsm',
  appCode: 'suiyue_pc',
  passwordLoginType: '9',
  qrcodeLoginType: '10',
  captchaAppletOrgCode: '',
  smsUpdatePwdAction: 'SD_UPDATAPWD_MSG',
  smsUpdatePwdParamString: '{"code":null,"product":"社区随约服务网上驿站"}',
  terminalOptions: '1,3,4',
  qrcodeBgImages: DEFAULT_QRCODE_BG_IMAGES.join(','),
}

/** 租户 extra 默认门户参数 */
export function createTenantPortalExtra() {
  return {
    ...fieldsToDefaults(PORTAL_LEGACY_BASE_FIELDS),
    copyRightInfo: '',
    systemLogo: '',
    loginBackground: '',
    ...fieldsToDefaults(PORTAL_COLOR_FIELDS),
    ...fieldsToDefaults(PORTAL_LOGIN_EXTRA_FIELDS),
    ...fieldsToDefaults(PORTAL_SERVICE_FIELDS),
    ...fieldsToDefaults(PORTAL_AUTH_FIELDS),
    ...fieldsToDefaults(PORTAL_ACTION_FIELDS),
    ...fieldsToDefaults(PORTAL_LAYOUT_FIELDS),
  }
}

/** hostname 是否包含关键词（登录框图备用等仍可用） */
export function hostMatches(keyword, hostname) {
  const host = (hostname != null ? hostname : (typeof window !== 'undefined' ? window.location.hostname : '') || '').toLowerCase()
  const key = (keyword || '').trim().toLowerCase()
  if (!key) return false
  return host.indexOf(key) > -1
}

export function resolveByHost(primary, alt, altHost, hostname) {
  return hostMatches(altHost, hostname) ? (alt != null && alt !== '' ? alt : primary) : primary
}

/** 将 terminalOptions（label 逗号串 / 旧 JSON）解析为 [{label,text}] */
export function resolveTerminalOptions(terminalOptions, _hostname) {
  const fallback = [
    { label: '1', text: 'TV' },
    { label: '3', text: 'APP' },
    { label: '4', text: '随约小程序' },
  ]
  if (terminalOptions == null || terminalOptions === '') return fallback

  // 新格式：逗号分隔 label
  if (typeof terminalOptions === 'string' && terminalOptions.indexOf('[') === -1) {
    const labels = terminalOptions.split(',').map(s => s.trim()).filter(Boolean)
    if (!labels.length) return fallback
    const map = TERMINAL_OPTION_CATALOG.reduce((acc, o) => {
      acc[o.label] = o
      return acc
    }, {})
    return labels.map(l => map[l] || { label: l, text: l })
  }

  // 兼容旧 terminalOptionsJson：[{host,options}] 或 [{label,text}]
  try {
    const parsed = typeof terminalOptions === 'string' ? JSON.parse(terminalOptions) : terminalOptions
    if (!Array.isArray(parsed) || !parsed.length) return fallback
    if (parsed[0] && parsed[0].options) {
      const emptyHost = parsed.find(i => !(i.host || '').trim())
      return Array.isArray(emptyHost && emptyHost.options) ? emptyHost.options : (parsed[0].options || fallback)
    }
    if (parsed[0] && parsed[0].label != null) return parsed
  } catch (e) {
    // ignore
  }
  return fallback
}

/** 解析二维码宣传背景图组（逗号串 / 数组）；空则默认 pic_hd1~6；最多 30 张 */
export function parseQrcodeBgImages(raw) {
  const max = 30
  if (Array.isArray(raw)) {
    const list = raw.map(s => String(s == null ? '' : s).trim()).filter(Boolean).slice(0, max)
    return list.length ? list : DEFAULT_QRCODE_BG_IMAGES.slice()
  }
  if (raw == null || String(raw).trim() === '') {
    return DEFAULT_QRCODE_BG_IMAGES.slice()
  }
  const list = String(raw).split(',').map(s => s.trim()).filter(Boolean).slice(0, max)
  return list.length ? list : DEFAULT_QRCODE_BG_IMAGES.slice()
}

/** 业务页读取：优先 sessionStorage（门户登录/进系统时写入） */
export function getCachedQrcodeBgImages() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const cache = window.sessionStorage.getItem('qrcodeBgImages')
      if (cache) {
        const parsed = JSON.parse(cache)
        if (Array.isArray(parsed) && parsed.length) {
          return parsed.map(s => String(s).trim()).filter(Boolean)
        }
      }
    }
  } catch (e) {
    // ignore
  }
  return DEFAULT_QRCODE_BG_IMAGES.slice()
}

/** 二维码应用编码（createWxAppletQr 的 appCode）；空表示不传 */
export function getCachedWxAppletAppCode() {
  try {
    if (typeof window !== 'undefined' && window.sessionStorage) {
      const v = window.sessionStorage.getItem('wxAppletAppCode')
      if (v != null && String(v).trim() !== '') return String(v).trim()
    }
  } catch (e) {
    // ignore
  }
  return ''
}

/** 旧字段迁移：terminalOptionsJson → terminalOptions */
export function migrateTerminalOptions(extra = {}) {
  if (extra.terminalOptions && String(extra.terminalOptions).trim()) {
    return String(extra.terminalOptions).trim()
  }
  if (!extra.terminalOptionsJson) return '1,3,4'
  try {
    const parsed = typeof extra.terminalOptionsJson === 'string'
      ? JSON.parse(extra.terminalOptionsJson)
      : extra.terminalOptionsJson
    if (!Array.isArray(parsed)) return '1,3,4'
    let options = []
    if (parsed[0] && parsed[0].options) {
      const emptyHost = parsed.find(i => !(i.host || '').trim())
      options = (emptyHost && emptyHost.options) || parsed[0].options || []
    } else {
      options = parsed
    }
    const labels = options.map(o => o.label).filter(Boolean)
    return labels.length ? labels.join(',') : '1,3,4'
  } catch (e) {
    return '1,3,4'
  }
}

function parseBgimages(para = {}) {
  if (Array.isArray(para.bgimages) && para.bgimages.length) {
    return para.bgimages.map(s => String(s).trim()).filter(Boolean)
  }
  const raw = para.systemImage || para.loginBackground || para.bgimages || ''
  if (raw && String(raw).trim()) {
    return String(raw).split(',').map(s => s.trim()).filter(Boolean)
  }
  return ['/images/bg.jpg']
}

/**
 * 保存前规范化：兼容字段互相同步，保证服务端存全量 KV
 * @param {object} extra
 * @param {{ oemName?: string }} tenantMeta 子平台名称等
 */
export function normalizeTenantPortalExtra(extra = {}, tenantMeta = {}) {
  const next = { ...createTenantPortalExtra(), ...extra }

  const copyright = (next.copyright || next.copyRightInfo || '').trim()
  next.copyright = copyright
  next.copyRightInfo = copyright

  const loginLogo = next.systemLogo || next.loginLogo || ''
  next.systemLogo = loginLogo
  next.loginLogo = loginLogo
  // sysLogo 不单独维护，与 loginLogo 同源（getDomainParaJson 返回）
  next.sysLogo = loginLogo
  next.favicon = (next.favicon || '').trim()
  next.wxAppletAppCode = (next.wxAppletAppCode || '').trim()
  delete next.appOrgCode

  const bg = next.systemImage || next.loginBackground || ''
  next.systemImage = bg
  next.loginBackground = bg
  // 应用列表封面独立；空则用登录背景第一张
  const listBg = (next.sysImage || '').trim()
  next.sysImage = (listBg && listBg.indexOf(',') === -1)
    ? listBg
    : (listBg.split(',').map(s => s.trim()).filter(Boolean)[0]
      || bg.split(',').map(s => s.trim()).filter(Boolean)[0]
      || '')

  next.domain = (next.domain || '').trim()

  // 全局 oemCode：合并历史拆分字段，只保留一份
  const oemCode = (next.oemCode || next.mainOemCode || next.orgApiOemCode || next.smsUpdatePwdOemCode || '').trim()
  next.oemCode = oemCode

  if (tenantMeta.oemName && !(next.oemName || '').trim()) {
    next.oemName = tenantMeta.oemName
  }

  next.terminalOptions = migrateTerminalOptions(next)
  next.qrcodeBgImages = parseQrcodeBgImages(next.qrcodeBgImages).join(',')

  // 历史 forgetPwdAction 并入 smsUpdatePwdAction（发短信与校验共用）
  if (!(next.smsUpdatePwdAction || '').trim() && (next.forgetPwdAction || '').trim()) {
    next.smsUpdatePwdAction = String(next.forgetPwdAction).trim()
  }

  // 已废弃：按域名关键词切换备用 Action / orgCode；多份 oemCode 拆分字段
  ;[
    'terminalOptionsJson',
    'captchaAppletOrgHost',
    'smsUpdatePwdActionAlt',
    'smsUpdatePwdParamStringAlt',
    'smsUpdatePwdActionAltHost',
    'forgetPwdAction',
    'forgetPwdActionAlt',
    'forgetPwdActionAltHost',
    'smsUpdatePwdOemCode',
    'orgApiOemCode',
    'mainOemCode',
    'loginPanelBgAlt',
    'loginPanelBgAltHost',
  ].forEach((k) => { delete next[k] })

  // 全部值转字符串，便于 form/Map 落库
  Object.keys(next).forEach((k) => {
    if (next[k] == null) next[k] = ''
    else if (typeof next[k] !== 'string') next[k] = String(next[k])
  })

  return next
}

export function hydrateTenantPortalExtra(extra = {}) {
  return normalizeTenantPortalExtra(extra || {})
}

/**
 * 旧 getByDomain 扁平结构 → 门户 oemInfo（补齐默认字段与 bgimages）
 * @param {object} data tenantPortalConfig
 */
export function normalizeLegacyOemInfo(data) {
  if (!data) {
    return { ...createTenantPortalExtra(), bgimages: ['/images/bg.jpg'] }
  }
  const defaults = createTenantPortalExtra()
  const next = { ...defaults, ...data }
  const loginLogo = next.loginLogo || next.systemLogo || ''
  next.loginLogo = loginLogo
  next.systemLogo = loginLogo
  next.sysLogo = loginLogo
  next.favicon = (next.favicon || '').trim()
  next.wxAppletAppCode = (next.wxAppletAppCode || '').trim()
  delete next.appOrgCode
  next.copyright = next.copyright || next.copyRightInfo || ''
  next.copyRightInfo = next.copyright
  next.bgimages = parseBgimages(next)
  next.systemImage = next.systemImage || next.bgimages.join(',')
  next.loginBackground = next.loginBackground || next.systemImage
  const listBg = (next.sysImage || '').trim()
  next.sysImage = (listBg && listBg.indexOf(',') === -1)
    ? listBg
    : (listBg.split(',').map(s => s.trim()).filter(Boolean)[0]
      || (next.bgimages[0] || '')
      || next.systemImage.split(',').map(s => s.trim()).filter(Boolean)[0]
      || '')
  next.oemCode = (next.oemCode || next.mainOemCode || next.orgApiOemCode || next.smsUpdatePwdOemCode || '').trim()
  const showBox = next.showServiceBox === '1' || next.showServiceBox === 1 || next.id == 1 || next.id === '1'
  next.id = showBox ? 1 : (next.id != null && next.id !== '' ? Number(next.id) || 0 : 0)
  next.showServiceBox = showBox ? '1' : (next.showServiceBox || '0')
  next.terminalOptions = migrateTerminalOptions(next)
  return next
}

/**
 * 将 getDomainParaJson 结果转为门户 oemInfo（兼容旧 getByDomain 扁平结构）
 * @param {object} data DomainParaVo
 */
export function toPortalOemInfo(data) {
  if (!data) return { ...createTenantPortalExtra(), bgimages: ['/images/bg.jpg'] }
  const defaults = createTenantPortalExtra()
  const para = { ...defaults, ...(data.paraJson || {}) }
  const loginLogo = para.loginLogo || para.systemLogo || ''
  const sysLogo = loginLogo
  const favicon = (para.favicon || '').trim()
  const wxAppletAppCode = (para.wxAppletAppCode || '').trim()
  const copyright = para.copyright || para.copyRightInfo || ''
  const bgimages = parseBgimages(para)
  const oemName = data.systemName || para.oemName || para.systemName || ''
  const showBox = para.showServiceBox === '1' || para.showServiceBox === 1 || para.id == 1 || para.id === '1'
  // 全局唯一 oemCode：优先配置项，其次接口根级；兼容旧 mainOemCode 等
  const oemCode = (para.oemCode || data.oemCode || para.mainOemCode || para.orgApiOemCode || para.smsUpdatePwdOemCode || '').trim()
  const loginBgJoined = para.systemImage || bgimages.join(',')
  const listBgRaw = (para.sysImage || '').trim()
  const sysImage = (listBgRaw && listBgRaw.indexOf(',') === -1)
    ? listBgRaw
    : (listBgRaw.split(',').map(s => s.trim()).filter(Boolean)[0]
      || (bgimages[0] || '')
      || loginBgJoined.split(',').map(s => s.trim()).filter(Boolean)[0]
      || '')
  delete para.appOrgCode

  return {
    ...para,
    // 旧接口根级字段
    id: showBox ? 1 : (para.id != null && para.id !== '' ? Number(para.id) || 0 : 0),
    tenantId: para.tenantId || data.tenantSystemId || '',
    domain: para.domain || '',
    copyright,
    loginLogo,
    systemLogo: loginLogo,
    sysLogo,
    favicon,
    wxAppletAppCode,
    oemName,
    oemCode,
    bgimages,
    systemImage: loginBgJoined,
    loginBackground: para.loginBackground || loginBgJoined,
    sysImage,
    // DomainPara 元数据
    systemName: data.systemName || oemName,
    systemType: data.systemType,
    systemId: data.systemId,
    tenantSystemId: data.tenantSystemId,
    // appCode：登录应用编码；systemCode：子系统编码（接口根级优先）
    appCode: data.appCode || para.appCode || 'suiyue_pc',
    systemCode: data.systemCode || para.systemCode || '',
    showServiceBox: showBox ? '1' : (para.showServiceBox || '0'),
    headerBgcolor: para.headerBgcolor,
    headerFontcolor: para.headerFontcolor,
    leftMenuBgcolor: para.leftMenuBgcolor,
    leftMenuFontcolor: para.leftMenuFontcolor,
    leftMenuSelectedBgcolor: para.leftMenuSelectedBgcolor,
    leftMenuSelecteFontcolor: para.leftMenuSelecteFontcolor,
    leftMenuitemBgcolor: para.leftMenuitemBgcolor,
  }
}

/** 解析登录后 sysConfigMap：兼容 PortalParam / IndexParam JSON + 扁平 KV */
export function normalizeSysConfigMap(sysConfigMap) {
  if (!sysConfigMap || typeof sysConfigMap !== 'object') return {}
  const flat = { ...sysConfigMap }
  let merged = {}
  const raw = flat.PortalParam != null ? flat.PortalParam : flat.IndexParam
  if (raw != null && String(raw).trim() !== '') {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        merged = { ...parsed }
      }
    } catch (e) {
      // ignore invalid PortalParam/IndexParam
    }
  }
  Object.keys(flat).forEach((k) => {
    if (k === 'PortalParam' || k === 'IndexParam') return
    const v = flat[k]
    if (v != null && String(v).trim() !== '') {
      merged[k] = v
    }
  })
  const logo = (merged.sysLogo || merged.systemLogo || merged.loginLogo || '').trim()
  if (logo && !(merged.sysLogo || '').trim()) {
    merged.sysLogo = logo
  }
  return merged
}

export function isValidHexColor(value) {
  if (!value) return true
  return /^#[0-9A-Fa-f]{6}$/.test(value)
}

/** 授权系统 paraJson（含门户全量字段，按 systemIndex 域名匹配） */
export function createSystemPortalParaJson() {
  const tenantDefaults = createTenantPortalExtra()
  // 系统级用 systemIndex 匹配域名，不强制 tenant.domain
  delete tenantDefaults.domain
  return {
    systemName: '',
    systemCode: '',
    systemIndex: '',
    systemLogo: '',
    systemImage: '',
    sysHomePage: '/services',
    sysLogo: '',
    sysImage: '',
    domain: '',
    isThirdSystem: 'false',
    menuApi: '',
    roleCodeApi: '',
    ...tenantDefaults,
    singleLogin: '0',
    overtimeSignOut: '30',
    visitVerification: '0',
    allowIps: '',
    remark: '',
    defaultHomePage: '/homepage',
    defaultMenuIcon: '/images/nopic.png',
  }
}

export function normalizeSystemPortalParaJson(para = {}) {
  let source = { ...(para || {}) }
  // 兼容库里只存了 PortalParam / IndexParam 大 JSON 的情况
  ;['PortalParam', 'IndexParam'].forEach((k) => {
    if (source[k] && typeof source[k] === 'string') {
      try {
        const parsed = JSON.parse(source[k])
        if (parsed && typeof parsed === 'object') {
          source = { ...parsed, ...source }
        }
      } catch (e) { /* ignore */ }
      delete source[k]
    }
  })
  const next = { ...createSystemPortalParaJson(), ...source }
  next.systemIndex = (next.systemIndex || '').trim()
  // 登录品牌 logo：loginLogo ↔ systemLogo
  next.systemLogo = next.systemLogo || next.loginLogo || ''
  next.loginLogo = next.loginLogo || next.systemLogo || ''
  // sysLogo 不单独维护，与 loginLogo 同源
  next.sysLogo = next.loginLogo || next.systemLogo || ''
  next.favicon = (next.favicon || '').trim()
  next.wxAppletAppCode = (next.wxAppletAppCode || '').trim()
  delete next.appOrgCode
  // 登录页背景（可多图）与应用列表封面（单图）分开维护
  const loginBg = next.systemImage || next.loginBackground || ''
  next.systemImage = loginBg
  next.loginBackground = loginBg
  // 应用列表封面：优先独立 sysImage；空则取登录背景第一张兜底
  const listBg = (next.sysImage || '').trim()
  if (listBg && listBg.indexOf(',') === -1) {
    next.sysImage = listBg
  } else if (listBg && listBg.indexOf(',') > -1) {
    next.sysImage = listBg.split(',').map(s => s.trim()).filter(Boolean)[0] || ''
  } else {
    next.sysImage = loginBg.split(',').map(s => s.trim()).filter(Boolean)[0] || ''
  }
  next.sysHomePage = next.sysHomePage || next.defaultHomePage || '/services'

  // 旧字段 → 新字段
  if (!next.headerBgcolor && next.topBackColor) next.headerBgcolor = next.topBackColor
  if (!next.headerFontcolor && next.topWordColor) next.headerFontcolor = next.topWordColor
  if (!next.headerSelectedBgcolor && next.topChooseBackColor) next.headerSelectedBgcolor = next.topChooseBackColor
  if (!next.headerSelectedFontcolor && next.topChooseWordColor) next.headerSelectedFontcolor = next.topChooseWordColor
  if (!next.leftMenuBgcolor && next.leftBackColor) next.leftMenuBgcolor = next.leftBackColor
  if (!next.leftMenuFontcolor && next.leftWordColor) next.leftMenuFontcolor = next.leftWordColor
  if (!next.leftMenuSelectedBgcolor && next.leftChooseBackColor) next.leftMenuSelectedBgcolor = next.leftChooseBackColor
  if (!next.leftMenuSelecteFontcolor && next.leftChooseWordColor) next.leftMenuSelecteFontcolor = next.leftChooseWordColor

  // 新字段 → 旧字段（兼容仍读 top*/left* 的消费方）
  next.topBackColor = next.headerBgcolor || next.topBackColor || ''
  next.topWordColor = next.headerFontcolor || next.topWordColor || ''
  next.topChooseBackColor = next.headerSelectedBgcolor || next.topChooseBackColor || ''
  next.topChooseWordColor = next.headerSelectedFontcolor || next.topChooseWordColor || ''
  next.leftBackColor = next.leftMenuBgcolor || next.leftBackColor || ''
  next.leftWordColor = next.leftMenuFontcolor || next.leftWordColor || ''
  next.leftChooseBackColor = next.leftMenuSelectedBgcolor || next.leftChooseBackColor || ''
  next.leftChooseWordColor = next.leftMenuSelecteFontcolor || next.leftChooseWordColor || ''

  next.terminalOptions = migrateTerminalOptions(next)
  next.qrcodeBgImages = parseQrcodeBgImages(next.qrcodeBgImages).join(',')
  next.oemCode = (next.oemCode || next.mainOemCode || next.orgApiOemCode || next.smsUpdatePwdOemCode || '').trim()
  if (!(next.smsUpdatePwdAction || '').trim() && (next.forgetPwdAction || '').trim()) {
    next.smsUpdatePwdAction = String(next.forgetPwdAction).trim()
  }
  ;[
    'terminalOptionsJson',
    'captchaAppletOrgHost',
    'smsUpdatePwdActionAlt',
    'smsUpdatePwdParamStringAlt',
    'smsUpdatePwdActionAltHost',
    'forgetPwdAction',
    'forgetPwdActionAlt',
    'forgetPwdActionAltHost',
    'smsUpdatePwdOemCode',
    'orgApiOemCode',
    'mainOemCode',
    'loginPanelBgAlt',
    'loginPanelBgAltHost',
  ].forEach((k) => { delete next[k] })

  Object.keys(next).forEach((k) => {
    if (next[k] == null) next[k] = ''
    else if (typeof next[k] !== 'string') next[k] = String(next[k])
  })
  return next
}

export const TENANT_PORTAL_FORM_SECTIONS = [
  { key: 'legacy', title: '基础定制（原 getByDomain）', tip: 'domain/copyright/loginLogo/主题色等历史字段', fields: PORTAL_LEGACY_BASE_FIELDS },
  { key: 'color', title: '主题颜色', tip: '顶栏/侧栏', fields: PORTAL_COLOR_FIELDS },
  { key: 'login', title: '登录页扩展', tip: '', fields: PORTAL_LOGIN_EXTRA_FIELDS },
  { key: 'service', title: '客服与申请通道', tip: '', fields: PORTAL_SERVICE_FIELDS },
  { key: 'auth', title: '登录鉴权', tip: '', fields: PORTAL_AUTH_FIELDS },
  { key: 'action', title: '业务 Action', tip: '重置密码 / 终端 / 二维码背景图组', fields: PORTAL_ACTION_FIELDS },
  { key: 'layout', title: '布局与其它', tip: '', fields: PORTAL_LAYOUT_FIELDS },
]
